import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
   Box,
   Button,
   Typography,
   Accordion,
   AccordionSummary,
   AccordionDetails,
   Chip,
   CircularProgress,
   Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StyledDashboardLayout from '../../Layout/style';
import { useGetContractTypeByIdQuery } from '../../Redux/Service/ContractTypeApi';
import { FIELD_CONFIG, convertDataToText } from '../../Utils/contractFieldHelpers';

const FIELD_NAMES = Object.keys(FIELD_CONFIG);

function ContractTypeView() {
   const navigate = useNavigate();
   const { id } = useParams();

   const { data: contractTypeResponse, isLoading, isError } = useGetContractTypeByIdQuery(id);
   const contractType = contractTypeResponse?.response;

   // Convert field_values array to object for easier lookup
   const fieldValuesMap = React.useMemo(() => {
      if (!contractType?.field_values || !Array.isArray(contractType.field_values)) {
         return {};
      }

      const map = {};
      contractType.field_values.forEach((field) => {
         map[field.field_name] = field.data;
      });
      return map;
   }, [contractType?.field_values]);

   if (isLoading) {
      return (
         <StyledDashboardLayout>
            <div className="content-wrapper">
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'center',
                     alignItems: 'center',
                     minHeight: '400px',
                  }}
               >
                  <CircularProgress />
               </Box>
            </div>
         </StyledDashboardLayout>
      );
   }

   if (isError || !contractType) {
      return (
         <StyledDashboardLayout>
            <div className="content-wrapper">
               <div className="content-header">
                  <div className="container-fluid">
                     <div className="d-flex justify-content-between mb-2">
                        <div className="pageTitle">
                           <h1 className="m-0">View Contract Type</h1>
                        </div>
                     </div>
                  </div>
               </div>
               <div className="card m-3 customCard">
                  <Box sx={{ p: 3 }}>
                     <Typography color="error" sx={{ mb: 2 }}>
                        Failed to load contract type. The contract type may not exist or there was
                        an error fetching the data.
                     </Typography>
                     <Button
                        onClick={() => navigate('/contract-type/list')}
                        variant="contained"
                        className="commonBtn"
                     >
                        Back to List
                     </Button>
                  </Box>
               </div>
            </div>
         </StyledDashboardLayout>
      );
   }

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="d-flex justify-content-between mb-2">
                     <div className="pageTitle">
                        <h1 className="m-0">View Contract Type</h1>
                     </div>

                     <Button
                        onClick={() => navigate('/contract-type/list')}
                        startIcon={<ArrowBackIcon />}
                        sx={{
                           display: 'flex',
                           alignItems: 'center',
                           gap: '8px',
                           padding: '10px 20px',
                           minWidth: '160px',
                           height: '44px',
                           backgroundColor: '#3F30FF',
                           borderRadius: '8px',
                           color: '#ffffff',
                           border: '2px solid #3F30FF',
                           textTransform: 'none',
                           fontSize: '14px',
                           fontWeight: '600',
                           letterSpacing: '0.5px',
                           boxShadow: '0 4px 12px rgba(63, 48, 255, 0.4)',
                           cursor: 'pointer',
                           transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                           position: 'relative',
                           overflow: 'hidden',
                           '&:before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: '-100%',
                              width: '100%',
                              height: '100%',
                              background:
                                 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                              transition: 'left 0.5s',
                           },
                           '&:hover': {
                              backgroundColor: '#3F30FF',
                              borderColor: '#3F30FF',
                              '&:before': {
                                 left: '100%',
                              },
                           },
                           '& .MuiButton-startIcon': {
                              marginRight: '8px',
                              fontSize: '18px',
                              color: '#ffffff',
                              transition: 'transform 0.3s ease',
                           },
                           '&:hover .MuiButton-startIcon': {
                              transform: 'translateX(-2px)',
                           },
                        }}
                     >
                        Back to Contract Types
                     </Button>
                  </div>
               </div>
            </div>

            <div className="card m-3 customCard">
               <Box sx={{ p: 3 }}>
                  {/* Basic Information Section */}
                  <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
                     <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#3F30FF' }}>
                        Basic Information
                     </Typography>

                     <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5 }}>
                           Contract Type Name
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                           {contractType.name}
                        </Typography>
                     </Box>

                     {contractType.description && (
                        <Box sx={{ mb: 2 }}>
                           <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5 }}>
                              Description
                           </Typography>
                           <Typography variant="body1">{contractType.description}</Typography>
                        </Box>
                     )}

                     <Box>
                        <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5 }}>
                           Status
                        </Typography>
                        <Chip
                           label={contractType.is_active ? 'Active' : 'Inactive'}
                           color={contractType.is_active ? 'success' : 'default'}
                           size="small"
                        />
                     </Box>
                  </Paper>

                  {/* Field Values Section */}
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 600, color: '#3F30FF' }}>
                     Pre-fill Field Values
                  </Typography>

                  <Box sx={{ mt: 2 }}>
                     {FIELD_NAMES.map((fieldName) => {
                        const fieldData = fieldValuesMap[fieldName];
                        const displayText = fieldData
                           ? convertDataToText(fieldData, fieldName)
                           : '';
                        const config = FIELD_CONFIG[fieldName];
                        const hasData = displayText.trim().length > 0;

                        return (
                           <Accordion
                              key={fieldName}
                              defaultExpanded={hasData}
                              sx={{
                                 mb: 1,
                                 '&:before': {
                                    display: 'none',
                                 },
                                 boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                              }}
                           >
                              <AccordionSummary
                                 expandIcon={<ExpandMoreIcon />}
                                 sx={{
                                    backgroundColor: hasData ? '#f0f7ff' : '#fafafa',
                                    '&:hover': {
                                       backgroundColor: hasData ? '#e3f2fd' : '#f5f5f5',
                                    },
                                 }}
                              >
                                 <Box
                                    sx={{
                                       display: 'flex',
                                       alignItems: 'center',
                                       gap: 1,
                                       width: '100%',
                                    }}
                                 >
                                    <Typography sx={{ fontWeight: 500 }}>{fieldName}</Typography>
                                    {!hasData && (
                                       <Chip label="No Data" size="small" color="default" />
                                    )}
                                 </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ backgroundColor: '#fff' }}>
                                 {hasData ? (
                                    <Box>
                                       <Paper
                                          sx={{
                                             p: 2,
                                             backgroundColor: '#f9f9f9',
                                             border: '1px solid #e0e0e0',
                                          }}
                                       >
                                          <Typography
                                             component="pre"
                                             sx={{
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                fontFamily: 'monospace',
                                                fontSize: '14px',
                                                margin: 0,
                                             }}
                                          >
                                             {displayText}
                                          </Typography>
                                       </Paper>
                                    </Box>
                                 ) : (
                                    <Typography
                                       variant="body2"
                                       sx={{ color: '#999', fontStyle: 'italic' }}
                                    >
                                       No pre-fill data configured for this field
                                    </Typography>
                                 )}
                              </AccordionDetails>
                           </Accordion>
                        );
                     })}
                  </Box>
               </Box>
            </div>
         </div>
      </StyledDashboardLayout>
   );
}

export default ContractTypeView;
